import { Box, LinearProgress } from "@mui/material";
import { usePostHog } from "@posthog/react";
import React, { useCallback, useEffect, useState } from "react";
import { capture } from "../analytics/posthog";
import ApiClient from "../config/ApiClient";
import { AthletePaymentAssignment, DirectoryMember, ModelName, PaginatedResponse } from "../object-actions/types/types";

interface AutoPaginatingListProps<T extends ModelName | AthletePaymentAssignment | DirectoryMember> {
    basePath: string;
    count: number;
    limit: number;
    offset: number;
    onSuccess?: (data: PaginatedResponse<T>) => void;
    onError?: (error: string) => void;
    children: React.ReactNode;
}


const AutoPaginatingList = <T extends ModelName | AthletePaymentAssignment | DirectoryMember>({
    basePath, limit, count, offset, onSuccess, onError, children
}: AutoPaginatingListProps<T>) => {
    const posthog = usePostHog();
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(async (newOffset = 0, newLimit = 10) => {

        if (loading) {
            console.log(`Loading pagination, skipping fetchData`);
            return;
        }

        if (newOffset > count) {
            console.log(`PAGINATION: All items loaded, skipping fetchData`);
            return;
        }

        setLoading(true);
        const [baseUrl, existingParams] = basePath.split("?");
        const params = new URLSearchParams(existingParams || "");
        params.set("offset", newOffset.toString());
        params.set("limit", newLimit.toString());
        const apiUrl = `${baseUrl}?${params.toString()}`;

        console.log(`Paginate ${apiUrl}`);
        const response = await ApiClient.get(apiUrl);
        if (response.error) {
            if (onError) onError(response.error);
        } else {
            if (onSuccess) onSuccess(response.data as PaginatedResponse<T>);
        }
        setLoading(false);

        capture(posthog, "auto_paginate_search", {
            event_category: "auto_paginate",
            event_label: "auto_paginate",
            value: apiUrl,
        });
    }, [basePath, limit, offset, onError, onSuccess, posthog]);

    // Scroll handler
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.innerHeight + window.scrollY;
            const bottomPosition = document.documentElement.scrollHeight;
            if (loading || count <= offset) return;
            if (bottomPosition - scrollPosition <= 150) {
                fetchData(offset + limit, limit);
            }
        };
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };

    }, [basePath, count, offset, limit, loading]);

    useEffect(() => {
        fetchData(offset, limit)
    }, [basePath]);

    return (
        <Box id="AutoPaginatingList">
            {children}
            {loading && <LinearProgress />}
        </Box>
    );
};

export default AutoPaginatingList;
